import React, { useState } from 'react';
import { Box, Tooltip, ClickAwayListener } from '@mui/material';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import { formatDay } from '../utils/formatDate';

/**
 * Omschrijving van een blessure/schorsing als tekst, voor de tooltip én voor
 * plekken (zoals de speler-pop-up) waar de info voluit getoond wordt.
 *
 * @param {object|null} status Het sidelined-record.
 * @return {string|null} Bijv. "Geschorst · 2 wedstrijd(en) · t/m 25 nov", of null.
 */
export function getSidelinedLabel(status) {
    if (!status) return null;

    if (status.category === 'suspension') {
        const parts = ['Geschorst'];
        if (status.gamesMissed) parts.push(`${status.gamesMissed} wedstrijd(en)`);
        if (status.endDate) parts.push(`t/m ${formatDay(status.endDate)}`);
        return parts.join(' · ');
    }

    const parts = [status.typeName || 'Geblesseerd'];
    if (status.endDate) parts.push(`terug: ${formatDay(status.endDate)}`);
    return parts.join(' · ');
}

/**
 * Icoon + tooltip voor een geblesseerde of geschorste speler.
 *
 * Verwacht een sidelined-record (`{ category, typeName, endDate, gamesMissed }`)
 * uit `seasons/{seasonId}/sidelined`. Zonder record wordt niets getoond.
 *
 * Gedeeld tussen het mobiele `PlayerCard` en de desktop-tabellen. De tooltip
 * opent op desktop bij hover en op mobiel (waar hover niet bestaat) bij een tik
 * op het icoon. De tik wordt tegengehouden (`stopPropagation`) zodat het
 * onderliggende kaartje/de rij niet óók reageert, en sluit weer bij een tik
 * ergens anders.
 *
 * @param {object} props
 * @param {object|null} props.status Het sidelined-record, of null.
 */
function SidelinedIcon({ status }) {
    const [open, setOpen] = useState(false);

    if (!status) return null;

    const title = getSidelinedLabel(status);
    // Een schorsing toont een rode kaart (rood rechthoekje), een blessure het
    // medische kruis — zo zijn ze los van elkaar herkenbaar.
    const anchor = status.category === 'suspension'
        ? <Box aria-label="Geschorst" sx={{ width: 11, height: 15, borderRadius: 0.5, bgcolor: '#e53935' }} />
        : <LocalHospitalIcon aria-label="Geblesseerd" sx={{ fontSize: 16, color: '#e53935' }} />;

    const handleClick = (e) => {
        e.stopPropagation();
        setOpen((prev) => !prev);
    };

    return (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Tooltip
                title={title}
                arrow
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => setOpen(false)}
                disableTouchListener
            >
                <Box
                    component="span"
                    onClick={handleClick}
                    sx={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer', flexShrink: 0 }}
                >
                    {anchor}
                </Box>
            </Tooltip>
        </ClickAwayListener>
    );
}

export default SidelinedIcon;
